import { CSSProperties, FC, useRef } from 'react';

import { CarouselRef } from 'antd/lib/carousel';

import { useBreakpoint } from '@contexts/breakpoints';
import CustomIcon from '@components/utils/CustomIcon';
import {
  SafeIcon,
  MediaImageLine,
  ArrowRightSLine,
  ArrowLeftSLine2,
} from '@assets/icons';
import { useToggle } from '@hooks/common';
import VideoModal from '@components/views/tour/components/video-modal';
import PlayVideoBadge from '@components/views/tour/components/video-badge';

import {
  Wrapper,
  IconWrapper,
  NextIcon,
  PrevIcon,
  StyledCarousel,
  SecureLabelWrapper,
  GalleryButton,
  GalleryCount,
  SeturVideoWrapper,
} from './styled';
import ItemImage from '../ListingItemImage/ItemImage';
import CloudinaryItemImage from '../CloudinaryItemImage';

export type LazyLoadTypes = 'ondemand' | 'progressive' | 'anticipated';
export interface SliderImage {
  imageUrl: string;
  alt: string;
  providerId?: number | null;
  placeholderUrl?: string | null;
}
interface Props {
  images: SliderImage[];
  videos?: string[];
  suggests?: boolean;
  secure?: boolean;
  fallBackImage?: string;
  seturExecutive?: boolean;
  showSliderIcon?: boolean;
  width?: string;
  imageStyle?: CSSProperties;
  onGalleryView?(): void;
  onImageClick?(): void;
  name?: string;
  sizes?: {
    width: number;
    height: number;
  };
  lazyLoad?: LazyLoadTypes;
  priority?: boolean;
}

const ListingItemSlider: FC<Props> = ({
  images,
  videos,
  secure,
  fallBackImage,
  showSliderIcon = true,
  width,
  imageStyle,
  onGalleryView,
  onImageClick,
  name,
  lazyLoad,
  priority,
}) => {
  const { xl } = useBreakpoint();

  const ref = useRef<CarouselRef>(null);

  const [visibleVideoModal, toggleVideoModal] = useToggle(false);

  const prev = () => {
    ref.current?.prev();
  };

  const next = () => {
    ref.current?.next();
  };

  const sliderImages =
    images && Array.isArray(images) && images?.length > 0
      ? images
      : [
          {
            imageUrl: fallBackImage,
            alt: 'setur',
          },
        ];

  const showSliderIcons =
    showSliderIcon && images && Array.isArray(images) && images?.length > 1
      ? true
      : false;

  return (
    <Wrapper>
      {secure ? (
        <SecureLabelWrapper>
          <CustomIcon icon={SafeIcon} fontSize={38} />
        </SecureLabelWrapper>
      ) : null}

      {videos && videos?.length > 0 ? (
        <SeturVideoWrapper>
          <PlayVideoBadge onClick={() => toggleVideoModal()} />
        </SeturVideoWrapper>
      ) : null}

      {showSliderIcons ? (
        <PrevIcon onClick={prev}>
          <IconWrapper>
            <CustomIcon
              fontSize={16}
              icon={ArrowLeftSLine2}
              paletteColor='grey1100'
            />
          </IconWrapper>
        </PrevIcon>
      ) : null}
      <StyledCarousel
        dots={!showSliderIcon}
        ref={ref}
        width={width}
        style={{
          height: '100%',
        }}
        rootClassName='carouselWrapper'
        lazyLoad={lazyLoad}
      >
        {sliderImages?.length ? (
          sliderImages?.map((item, index) => (
            <CloudinaryItemImage
              key={index}
              url={item.imageUrl}
              alt={item.alt || 'S&F'}
              fallBackImage={fallBackImage}
              width={xl ? 252 : 320}
              height={xl ? 350 : 200}
              onClick={onImageClick}
              priority={priority && index === 0}
              providerId={item.providerId}
              placeholderUrl={item.placeholderUrl}
            />
          ))
        ) : (
          <ItemImage
            key={0}
            imageSrc={fallBackImage}
            alt='S&F'
            sliderImage={true}
            imageStyle={imageStyle}
            onImageClick={onImageClick}
          />
        )}
      </StyledCarousel>
      {showSliderIcons ? (
        <NextIcon onClick={next}>
          <IconWrapper>
            <CustomIcon
              fontSize={16}
              icon={ArrowRightSLine}
              paletteColor='grey1100'
            />
          </IconWrapper>
        </NextIcon>
      ) : null}
      {images.length && onGalleryView ? (
        <GalleryButton onClick={onGalleryView}>
          <CustomIcon
            icon={MediaImageLine}
            fontSize={16}
            paletteColor='grey1100'
          />
          <GalleryCount>{images.length}</GalleryCount>
        </GalleryButton>
      ) : null}

      {visibleVideoModal ? (
        <VideoModal
          title={name ?? ''}
          visible={visibleVideoModal}
          toggleModal={toggleVideoModal}
          videoURL={videos?.[0]}
          isVideo={Boolean((videos?.length ?? 0) > 0)}
          fallBackImage={sliderImages?.[0]?.imageUrl}
        />
      ) : null}
    </Wrapper>
  );
};

export default ListingItemSlider;
